"use client";

import Link from "next/link";
import { MarkdownBody } from "@/components/MarkdownBody";
import { PageHero } from "@/components/layout/PageHero";
import { SitePageShell } from "@/components/layout/SitePageShell";
import { Reveal } from "@/components/motion";

interface LegalSection {
  heading: string;
  body: string;
}

interface LegalPageViewProps {
  eyebrow: string;
  title: string;
  lastUpdated: string;
  sections: readonly LegalSection[];
  /** True when CMS fetch failed and static fallback content is shown. */
  usingFallback?: boolean;
}

export function LegalPageView({
  eyebrow,
  title,
  lastUpdated,
  sections,
  usingFallback = false,
}: LegalPageViewProps) {
  return (
    <SitePageShell className="pb-16">
      <PageHero
        eyebrow={eyebrow}
        title={title}
        subtitle={`Last updated: ${lastUpdated}`}
        align="center"
      />

      <section className="site-section !pt-0">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          {usingFallback ? (
            <p
              className="mb-6 rounded-lg border border-[var(--site-border)] bg-[var(--site-surface-alt)] px-4 py-3 text-center text-sm text-[var(--site-muted)]"
              role="status"
            >
              Showing default copy — live content unavailable.
            </p>
          ) : null}
          <Reveal>
            <div className="space-y-8">
              {sections.map((s) => (
                <section key={s.heading} className="site-card p-6 md:p-8">
                  <h2 className="mb-3 font-display text-xl font-semibold text-[var(--site-primary)]">
                    {s.heading}
                  </h2>
                  <MarkdownBody content={s.body} />
                </section>
              ))}
            </div>
          </Reveal>
          <p className="mt-10 text-center">
            <Link
              href="/"
              className="text-sm font-medium text-[var(--site-accent)] hover:underline"
            >
              &larr; Back to Home
            </Link>
          </p>
        </div>
      </section>
    </SitePageShell>
  );
}
